import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ContentApp from '../../../../components/ContentApp';
import ContentBottom from '../../../../components/ContentBottom';
import InputFormBorder from '../../../../components/Common/InputFormBorder/InputFormBorder';
import DatePickerBorder from '../../../../components/Common/InputFormBorder/DatePickerBorder';
import ModalSelector from '../../../../components/ModalSelector/ModalSelector';
import TimePickerDual from '../../../../components/TimePicker/TimePicker';
import { formatDateTime } from '../../../../helpers/dates';
import Button from '../../../../components/Common/Button/Button';
import Header from '../../../../components/Common/Header/Header';
import airlinesData from '../../../../data/airlines.json';

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <div className='error-label text-center mb-3' style={{ position: 'relative' }}>
      {message}
    </div>
  );
};

const FlightSection = ({
  airline,
  setAirline,
  flightNumber,
  setFlightNumber,
  date,
  setDate,
  hour,
  setHour,
  minute,
  setMinute,
  t,
  getFormattedTime,
  errors = {},
  showFlightInputs = true,
  time,
  setTime,
  isInboundTrip = false,
}) => {
  const [airlinesOptions, setAirlinesOptions] = useState([]);

  useEffect(() => {
    if (showFlightInputs && airlinesData) {
      const formattedAirlines = airlinesData.map((airline, index) => ({
        value: airline.name,
        label: airline.name,
        id: index,
      }));
      setAirlinesOptions(formattedAirlines);
    }
  }, [showFlightInputs]);

  return (
    <>
      {showFlightInputs && (
        <>
          <ModalSelector
            placeholder={t('airline')}
            selectedValue={airline}
            onChange={setAirline}
            options={airlinesOptions}
          />
          <ErrorMessage
            message={errors.airline ? `${t('airline')} ${t('bankForm.isRequired')}` : ''}
          />

          <div className='mt-2 pt-1'>
            <InputFormBorder
              placeholder={t('flightNumber')}
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value)}
              error={errors.flightNumber}
            />
          </div>
        </>
      )}

      <DatePickerBorder
        placeholder={
          showFlightInputs
            ? isInboundTrip
              ? t('transfer.pickupDate')
              : t('transfer.flightDate')
            : t('transfer.pickupDate')
        }
        value={date || null}
        onChange={setDate}
      />
      <ErrorMessage message={errors.date ? `${t('date')} ${t('bankForm.isRequired')}` : ''} />

      <div
        className='time-selectors-container'
        style={{ display: 'flex', gap: '10px', marginTop: '15px' }}
      >
        <TimePickerDual
          value={time}
          placeholder={
            showFlightInputs
              ? isInboundTrip
                ? t('transfer.pickupTime')
                : t('transfer.flightTime')
              : t('transfer.pickupTime')
          }
          onChange={(val, str) => {
            setTime(val);
          }}
          minuteStep={5}
          enableSound
          enableVibration
          volume={0.25}
          minIntervalMs={80}
          wheelMode='normal'
          className='time-picker-dual'
        />
      </div>

      {getFormattedTime(hour, minute) && (
        <div className='mt-2 text-center'>
          <div className='font-tiny'>
            {t('selectedTime')}: {getFormattedTime(hour, minute)}
          </div>
        </div>
      )}
    </>
  );
};

const CompleteTransferDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const reservation = useSelector((state) => state.reservation);
  const [arrivalAirline, setArrivalAirline] = useState(null);
  const [arrivalFlightNumber, setArrivalFlightNumber] = useState('');
  const [arrivalDate, setArrivalDate] = useState(null);
  const [arrivalHour, setArrivalHour] = useState(null);
  const [arrivalMinute, setArrivalMinute] = useState(null);
  const [departureAirline, setDepartureAirline] = useState(null);
  const [departureFlightNumber, setDepartureFlightNumber] = useState('');
  const [departureDate, setDepartureDate] = useState(null);
  const [departureHour, setDepartureHour] = useState(null);
  const [departureMinute, setDepartureMinute] = useState(null);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({ arrival: {}, departure: {} });
  const isRoundTrip = reservation?.transferType === 'ROUNDTRIP';
  const [time, setTime] = useState({ hour: 14, minute: 0 });
  const [timeDeparture, setTimeDeparture] = useState({ hour: 14, minute: 0 });

  const hasAirport =
    reservation.transferFrom?.pointType === 'AIRPORT' ||
    reservation.transferTo?.pointType === 'AIRPORT';
  const showDepartureFlightInputs = isRoundTrip && hasAirport;

  const validateForm = () => {
    let valid = true;
    let newErrors = { arrival: {}, departure: {} };

    if (hasAirport) {
      if (!arrivalAirline || !arrivalAirline.value) {
        newErrors.arrival.airline = true;
        valid = false;
      }
      if (!arrivalFlightNumber) {
        newErrors.arrival.flightNumber = true;
        valid = false;
      }
    }
    if (!arrivalDate) {
      newErrors.arrival.date = true;
      valid = false;
    }
    // if (!arrivalHour) {
    //   newErrors.arrival.hour = true;
    //   valid = false;
    // }
    // if (arrivalMinute == null) {
    //   newErrors.arrival.minute = true;
    //   valid = false;
    // }

    if (isRoundTrip) {
      if (showDepartureFlightInputs) {
        if (!departureAirline || !departureAirline.value) {
          newErrors.departure.airline = true;
          valid = false;
        }
        if (!departureFlightNumber) {
          newErrors.departure.flightNumber = true;
          valid = false;
        }
      }
      if (!departureDate) {
        newErrors.departure.date = true;
        valid = false;
      }
      // if (!departureHour) {
      //   newErrors.departure.hour = true;
      //   valid = false;
      // }
      // if (departureMinute == null) {
      //   newErrors.departure.minute = true;
      //   valid = false;
      // }
    }

    setErrors(newErrors);
    return valid;
  };

  const getFormattedTime = (hour, minute) => {
    if (!hour || minute == null) return '';
    const [h, meridiem] = hour.value.split('-');
    return `${h}:${minute.label} ${meridiem}`;
  };

  const handleContinue = () => {
    if (!validateForm()) return;

    const trips = [
      {
        fromPickupId: reservation.transferFrom?.value,
        toPickupId: reservation.transferTo?.value,
        transferDate: formatDateTime(
          arrivalDate,
          time.hour.toString().padStart(2, '0') + ':' + time.minute.toString().padStart(2, '0'),
        ),
        airline: hasAirport ? arrivalAirline?.value || arrivalAirline : undefined,
        flightNumber: hasAirport ? arrivalFlightNumber : undefined,
      },
    ];

    if (isRoundTrip) {
      trips.push({
        fromPickupId: reservation.transferFrom?.value,
        toPickupId: reservation.transferTo?.value,
        transferDate: formatDateTime(
          departureDate,
          timeDeparture.hour.toString().padStart(2, '0') +
            ':' +
            timeDeparture.minute.toString().padStart(2, '0'),
        ),
        airline: showDepartureFlightInputs
          ? departureAirline?.value || departureAirline
          : undefined,
        flightNumber: showDepartureFlightInputs ? departureFlightNumber : undefined,
      });
    }

    const reservationTransferRequest = {
      fromZoneId: reservation.transferFrom?.zoneId,
      toZoneId: reservation.transferTo?.zoneId,
      transferType: reservation.transferType,
      trips,
    };

    dispatch({
      type: 'RESERVATION_INFO',
      payload: {
        ...reservation,
        reservationTransferRequest,
      },
    });

    history.push('/aditional-transfer-information');
  };

  return (
    <>
      <Header />

      <ContentApp classes='pb-0 fade-up'>
        <div>
          <div className='fw-600 font-small'>
            {hasAirport ? t('arrivalInformation') : t('transfer.outboundTransfer')}
          </div>

          <div
            className='font-tiny text-tertiary mt-1'
            dangerouslySetInnerHTML={{
              __html: hasAirport
                ? t('transfer.instructionsArrivalFlight')
                : t('transfer.serviceDetails', {
                    pickup: reservation.transferFrom?.label,
                  }),
            }}
          />

          <div className='mt-3'>
            <FlightSection
              title='arrivalInformation'
              airline={arrivalAirline}
              setAirline={setArrivalAirline}
              flightNumber={arrivalFlightNumber}
              setFlightNumber={setArrivalFlightNumber}
              date={arrivalDate}
              setDate={setArrivalDate}
              hour={arrivalHour}
              setHour={setArrivalHour}
              minute={arrivalMinute}
              setMinute={setArrivalMinute}
              t={t}
              getFormattedTime={getFormattedTime}
              errors={errors.arrival}
              showFlightInputs={hasAirport}
              time={time}
              setTime={setTime}
            />
          </div>

          {isRoundTrip && (
            <>
              <div className='fw-600 font-small mt-4'>
                {hasAirport ? t('departureInformation') : t('transfer.inboundTransfer')}
              </div>

              <div
                className='font-tiny text-tertiary mt-1'
                dangerouslySetInnerHTML={{
                  __html: hasAirport
                    ? t('transfer.instructionsDepartureFlight', {
                        pickup: reservation.transferTo?.label,
                      })
                    : t('transfer.serviceDetails', {
                        pickup: reservation.transferTo?.label,
                      }),
                }}
              />

              <div className='mt-3'>
                <FlightSection
                  title='departureInformation'
                  airline={departureAirline}
                  setAirline={setDepartureAirline}
                  flightNumber={departureFlightNumber}
                  setFlightNumber={setDepartureFlightNumber}
                  date={departureDate}
                  setDate={setDepartureDate}
                  hour={departureHour}
                  setHour={setDepartureHour}
                  minute={departureMinute}
                  setMinute={setDepartureMinute}
                  t={t}
                  getFormattedTime={getFormattedTime}
                  errors={errors.departure}
                  showFlightInputs={showDepartureFlightInputs}
                  time={timeDeparture}
                  setTime={setTimeDeparture}
                  isInboundTrip={true}
                />
              </div>
            </>
          )}
        </div>
      </ContentApp>

      <div className='mt-5'>
        <ContentBottom>
          <Button
            text={t('continue')}
            size='large'
            onClick={handleContinue}
            icon={true}
            typeIcon='arrow'
            iconColor='#000'
            disabled={false}
          />
        </ContentBottom>
      </div>
    </>
  );
};

export default CompleteTransferDetails;
